/**
 * Universal Domain Repository Hook
 * Profile-driven hook that replaces per-domain repository hooks.
 * Uses DomainDataProfile to auto-detect field mappings and build hierarchies.
 */

import { useMemo } from 'react';
import { useExternalRecords } from './useDataSources';
import { detectFieldsFromProfile } from '@/core/data-profiles';
import { buildHierarchyMaps } from '@/core/data-profiles';
import type { HierarchyMaps } from '@/core/data-profiles';
import type { DomainDataProfile } from '@/core/data-profiles';
import type { GraphNode } from '../../../lib/graph/types';

export interface RawExternalRecord {
  id: string;
  data: Record<string, unknown>;
}

export interface UseDomainRepositoryOptions<TRecord = RawExternalRecord> {
  sourceId: string | undefined;
  profile: Pick<DomainDataProfile, 'fieldAliases' | 'hierarchyConfig'>;
  filterText?: string;
  fieldMappingOverrides?: Partial<Record<string, string>>;
  limit?: number;
  recordTransform?: (
    raw: RawExternalRecord,
    mapping: Record<string, string | null>
  ) => TRecord;
}

export interface UseDomainRepositoryResult<TRecord = RawExternalRecord> {
  records: TRecord[];
  rawRecords: RawExternalRecord[];
  hierarchy: HierarchyMaps | null;
  fieldMapping: Record<string, string | null>;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  totalCount: number;
  hasMore: boolean;
}

/**
 * Universal hook for fetching external records and building domain-specific structures.
 *
 * @example
 * ```tsx
 * // Basic usage with a profile
 * const { records, hierarchy, fieldMapping, isLoading } = useDomainRepository({
 *   sourceId: dataSource?.id,
 *   profile: OrgChartProfile,
 * });
 *
 * // With custom record transform
 * const { records } = useDomainRepository({
 *   sourceId,
 *   profile: ProcessProfile,
 *   recordTransform: (raw, mapping) => ({
 *     id: String(raw.data[mapping.processId ?? 'id'] ?? raw.id),
 *     name: String(raw.data[mapping.name ?? 'name'] ?? 'Unnamed'),
 *     data: raw.data,
 *   }),
 * });
 * ```
 */
export function useDomainRepository<TRecord = RawExternalRecord>(
  options: UseDomainRepositoryOptions<TRecord>
): UseDomainRepositoryResult<TRecord> {
  const {
    sourceId,
    profile,
    fieldMappingOverrides,
    limit = 500,
    recordTransform,
  } = options;

  const {
    data: recordsData,
    isLoading,
    error,
    refetch,
  } = useExternalRecords(sourceId, { limit });

  const rawRecords: RawExternalRecord[] = recordsData?.records ?? [];

  // Auto-detect field mappings from first record's keys
  const detectedMapping = useMemo(() => {
    if (rawRecords.length === 0) return null;
    const headers = Object.keys(rawRecords[0].data);
    return detectFieldsFromProfile(headers, profile);
  }, [rawRecords, profile]);

  // Merge with user overrides
  const effectiveMapping = useMemo((): Record<string, string | null> => {
    if (!detectedMapping) return {};
    if (!fieldMappingOverrides) return detectedMapping;
    const merged: Record<string, string | null> = { ...detectedMapping };
    for (const [key, value] of Object.entries(fieldMappingOverrides)) {
      merged[key] = value ?? null;
    }
    return merged;
  }, [detectedMapping, fieldMappingOverrides]);

  // Transform records if a transform function is provided
  const records = useMemo((): TRecord[] => {
    if (rawRecords.length === 0) return [];
    if (!recordTransform) return rawRecords as unknown as TRecord[];
    return rawRecords.map((raw) => recordTransform(raw, effectiveMapping));
  }, [rawRecords, effectiveMapping, recordTransform]);

  // Build hierarchy if profile has hierarchyConfig
  const hierarchy = useMemo((): HierarchyMaps | null => {
    const config = profile.hierarchyConfig;
    if (!config || config.type === 'flat' || rawRecords.length === 0) {
      return null;
    }

    // Convert raw records to GraphNode-compatible format using field mapping
    const nodes = rawRecordsToGraphNodes(rawRecords, effectiveMapping);
    return buildHierarchyMaps(nodes, config);
  }, [rawRecords, effectiveMapping, profile.hierarchyConfig]);

  return {
    records,
    rawRecords,
    hierarchy,
    fieldMapping: effectiveMapping,
    isLoading,
    error: error as Error | null,
    refetch,
    totalCount: recordsData?.total ?? 0,
    hasMore: recordsData?.hasMore ?? false,
  };
}

/**
 * Convert raw external records to GraphNode format for hierarchy building.
 * Maps external field names to canonical field names using the field mapping.
 */
function rawRecordsToGraphNodes(
  records: RawExternalRecord[],
  mapping: Record<string, string | null>
): Array<GraphNode<string, Record<string, unknown>>> {
  // Build a reverse mapping: external column name → canonical field name
  const reverseMap = new Map<string, string>();
  for (const [canonical, external] of Object.entries(mapping)) {
    if (external) {
      reverseMap.set(external, canonical);
    }
  }

  return records.map((record) => {
    const props: Record<string, unknown> = {};

    // Map each data field to its canonical name
    for (const [key, value] of Object.entries(record.data)) {
      const canonical = reverseMap.get(key);
      if (canonical) {
        props[canonical] = value;
      } else {
        props[key] = value;
      }
    }

    // Determine the record ID using the first field in the profile's fieldAliases
    // that maps to a detected column, or fall back to the raw record ID
    const id = findRecordId(record, mapping);

    return { id, type: 'node', props };
  });
}

/**
 * Determine a record's ID from the mapping.
 * Looks for common ID field patterns in order of priority.
 */
function findRecordId(
  record: RawExternalRecord,
  mapping: Record<string, string | null>
): string {
  // Check for common ID fields in the mapping
  for (const idField of ['personId', 'processId', 'id']) {
    const column = mapping[idField];
    if (column && record.data[column] !== undefined) {
      return String(record.data[column]);
    }
  }
  return record.id;
}
